/**
 * Spanish (Argentina) strings.
 *
 * Terminology settled with the practice:
 *   - Company level (OSDE, IOMA, PAMI...) reads "Obra Social / Prepaga", since
 *     the list mixes both kinds and labelling them all "Prepaga" would be wrong.
 *   - The tier beneath it (310, SMG20, Oro...) is "Subcategoría".
 *   - A patient with no coverage is "Particular".
 *   - Clinical notes are "Historia Clínica".
 */

const es = {
  common: {
    appName: 'Gestión SRLO',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    add: 'Agregar',
    close: 'Cerrar',
    confirm: 'Confirmar',
    back: 'Volver',
    retry: 'Reintentar',
    // A request that got no answer at all, after reads were tried again
    // (lib/networkRetry.js). Shown by the database client as "name: message".
    offlineName: 'Sin conexión',
    offline: 'no pudimos llegar al servidor. Revisá tu conexión y volvé a intentar.',
    next: 'Siguiente',
    search: 'Buscar',
    loading: 'Cargando...',
    saving: 'Guardando...',
    noResults: 'Sin resultados',
    required: 'Obligatorio',
    optional: 'Opcional',
    yes: 'Sí',
    no: 'No',
    all: 'Todos',
    none: 'Ninguno',
    total: 'Total',
    date: 'Fecha',
    time: 'Hora',
    status: 'Estado',
    actions: 'Acciones',
    view: 'Ver',
    comingSoon: 'Esta sección está en construcción.',
    underAMinute: 'menos de 1 min',
    minutesShort: '{count} min',
    hoursMinutesShort: '{hours} h {minutes} min',
  },

  auth: {
    login: 'Iniciar sesión',
    logout: 'Cerrar sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    register: 'Registrarse',
    tagline: 'Gestión de consultorios psicológicos',
    newPatient: '¿Paciente nuevo? Registrate',
    noAccount: '¿No tenés cuenta?',
    loginFailed: 'No se pudo iniciar sesión',
    invalidCredentials: 'El correo o la contraseña no son correctos.',
    loggingIn: 'Iniciando sesión...',
    blockedTitle: 'Cuenta suspendida',
    blockedBody: 'Tu cuenta está bloqueada. Comunicate con la administración del consultorio.',
    forgotPassword: '¿Olvidaste tu contraseña?',
    areYouProfessional: '¿Sos profesional del consultorio?',
    activateHere: 'Activá tu cuenta',
    applyHere: 'Postulate',
    noProfileTitle: 'Tu cuenta todavía no está habilitada',
    noProfileBody: 'Iniciaste sesión, pero tu usuario no está asociado a ningún perfil. Si sos profesional, pedile al administrador que te dé de alta con este mismo correo y volvé a entrar. Si sos paciente, registrate desde el inicio.',
  },

  home: {
    greeting: 'Hola, {name}',
    todayAppointments: 'Turnos de hoy',
    todayAgenda: 'Agenda de hoy',
    patientSubtitle: 'Vos y tu grupo familiar',
    loadFailed: 'No pudimos cargar los datos',
    owesTitle: 'Pagos pendientes',
    owesLine: 'Tenés {amount} sin pagar con {professional}.',
    owesLineFor: '{person}: {amount} sin pagar con {professional}.',
    seePayments: 'Ver el detalle',
    pendingRequests: {
      one: 'Tenés 1 solicitud esperando tu respuesta.',
      other: 'Tenés {count} solicitudes esperando tu respuesta.',
    },
    reviewRequests: 'Revisar solicitudes',
    // Patient cancellations the professional has not seen (0059).
    patientCancellationsTitle: {
      one: '1 novedad de tus pacientes',
      other: '{count} novedades de tus pacientes',
    },
    // A patient reaching 3 or more cancellations or absences in a row (0061).
    repeatedCancellations: '{name} lleva {count} cancelaciones o ausencias seguidas.',
    patientCancelled: '{name} canceló el turno del {when}.',
    patientWithdrew: '{name} retiró el pedido de turno del {when}.',
    cancellationCharged: 'Queda con cargo.',
    cancellationNotCharged: 'Sin cargo.',
    cancellationReason: 'Motivo: {reason}',
    cancellationsSeen: 'Entendido',
    patientCancellationsFailed: 'No pudimos revisar si hubo cancelaciones de pacientes',
  },

  register: {
    title: 'Crear cuenta',
    subtitle: 'Registrate para reservar turnos con nuestros profesionales',
    submit: 'Crear cuenta',
    haveAccount: '¿Ya tenés cuenta?',
    successTitle: '¡Registro completado!',
    checkEmail: 'Te enviamos un correo para confirmar tu cuenta. Confirmala y después iniciá sesión.',
    readyToLogin: 'Ya podés iniciar sesión con tu correo y contraseña.',
    step: {
      personal: 'Datos personales',
      coverage: 'Cobertura',
      professional: 'Profesional',
    },
    firstName: 'Nombre',
    lastName: 'Apellido',
    confirmPassword: 'Repetir contraseña',
    passwordHint: 'Mínimo 8 caracteres',
    nationality: 'Nacionalidad',
    phone: 'Teléfono',
    address: 'Domicilio',
    emergencyContact: 'Contacto de emergencia',
    consentText: 'Presté conformidad al consentimiento informado y al tratamiento de mis datos personales.',
    fillRequired: 'Completá todos los campos obligatorios.',
    passwordTooShort: 'La contraseña debe tener al menos 8 caracteres.',
    passwordMismatch: 'Las contraseñas no coinciden.',
    consentRequired: 'Necesitamos tu conformidad con el consentimiento informado.',
    coverageRequired: 'Elegí una cobertura o seleccioná Particular.',
    coverageIntro: 'Indicá con qué cobertura vas a atenderte.',
    particularHint: 'Sin obra social ni prepaga. El valor se acuerda con el profesional.',
    selectCompany: 'Elegí una obra social o prepaga',
    selectPlan: 'Elegí la subcategoría',
    coverageMissingHint: 'Si no encontrás tu cobertura, elegí Particular y avisale a tu profesional.',
    professionalIntro: 'Elegí con quién querés atenderte. Podés cambiarlo más adelante.',
    chooseLater: 'Elegir más adelante',
    approvalNote: 'El profesional debe aprobar tu solicitud antes de que puedas reservar turnos.',
  },

masterData: {
    subtitleAdmin: 'Listas compartidas por todo el consultorio. Solo vos podés eliminar.',
    subtitleTherapist: 'Podés agregar lo que falte. Para eliminar, pedíselo al administrador.',
    tabCoverages: 'Coberturas',
    tabDisciplines: 'Disciplinas',
    tabRequests: 'Solicitudes',
    inactive: 'Inactivo',
    reactivate: 'Reactivar',
    duplicate: 'Ese nombre ya existe en la lista.',
    confirmRemove: '¿Eliminar "{name}"?',
    removed: 'Se eliminó "{name}".',
    deactivated: {
      one: '\"{name}\" quedó inactivo porque ya se usa en {count} registro. No aparece más para elegir, pero el historial se mantiene.',
      other: '\"{name}\" quedó inactivo porque ya se usa en {count} registros. No aparece más para elegir, pero el historial se mantiene.',
    },
    selectCompanyFirst: 'Elegí una obra social o prepaga para ver sus subcategorías',
    noPlans: 'Todavía no hay subcategorías cargadas',
    selectDisciplineFirst: 'Elegí una disciplina para ver sus especialidades',
    noSpecializations: 'Todavía no hay especialidades cargadas',
    requestRemoval: 'Solicitar eliminación',
    requestRemovalBody: 'Le vas a pedir al administrador que elimine "{name}". Contale por qué.',
    reason: 'Motivo',
    reasonPlaceholder: 'Por ejemplo: está duplicado, ya no trabajamos con esta cobertura...',
    sendRequest: 'Enviar solicitud',
    requestSent: 'Enviamos tu solicitud al administrador.',
    noPendingRequests: 'No hay solicitudes pendientes',
    resolvedRequests: 'Resueltas',
    // What a removal request removed, and whether anything used it (0066).
    nameNotKept: 'Sin nombre guardado',
    requestedBy: 'pedida por {name} el {when}',
    resolvedBy: 'Respondida por {name} el {when}.',
    usageCounting: 'Revisando si algo la usa…',
    usageFailed: 'No pudimos revisar si algo la usa. Al aprobar se revisa de nuevo: si algo la usa, queda inactiva en vez de borrarse.',
    usageGone: 'Ya no existe: al aprobar solo se cierra la solicitud.',
    usageNone: 'Nadie la usa: al aprobar se borra.',
    usageNoneSubcategories: {
      one: 'Nadie la usa: al aprobar se borra, con su subcategoría, que tampoco usa nadie.',
      other: 'Nadie la usa: al aprobar se borra, con sus {count} subcategorías, que tampoco usa nadie.',
    },
    usageSome: 'En uso: {uses}. Al aprobar queda inactiva: deja de aparecer para elegir y lo ya cargado no cambia.',
    usage: {
      professionals: { one: '1 profesional', other: '{count} profesionales' },
      patients: { one: '1 paciente', other: '{count} pacientes' },
      treatments: { one: '1 tratamiento', other: '{count} tratamientos' },
      sessions: { one: '1 turno', other: '{count} turnos' },
      payment_terms: { one: '1 plazo de pago', other: '{count} plazos de pago' },
      cancellation_fees: { one: '1 valor por cancelación', other: '{count} valores por cancelación' },
      specializations: { one: '1 especialidad', other: '{count} especialidades' },
      subscription_plans: { one: '1 cuota de suscripción', other: '{count} cuotas de suscripción' },
    },
    outcomeDeleted: 'Se borró: nadie la usaba.',
    outcomeDeactivated: 'Quedó inactiva. En uso: {uses}.',
    outcomeDeactivatedPlain: 'Quedó inactiva.',
    outcomeGone: 'Ya no existía cuando se aprobó.',
    deletedNotice: 'Se borró «{name}»: nadie la usaba.',
    deactivatedNotice: '«{name}» quedó inactiva: deja de aparecer para elegir y lo ya cargado no cambia. En uso: {uses}.',
    goneNotice: '«{name}» ya no existía: la solicitud quedó aprobada.',
    rejectedNotice: 'Rechazaste la solicitud de borrar «{name}».',
    approve: 'Aprobar',
    reject: 'Rechazar',
    entityType: {
      insurance_company: 'Obra Social / Prepaga',
      insurance_plan: 'Subcategoría',
      discipline: 'Disciplina',
      specialization: 'Especialidad',
    },
    status: { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' },
    // What each professional charges moved to Configuración → Cobros.
    goToCharges: 'Mis coberturas y valores',
  },

activate: {
    title: 'Activación de cuenta profesional',
    explanation: 'El administrador ya cargó tus datos. Elegí una contraseña con el mismo correo que le pasaste y tu cuenta queda vinculada sola.',
    emailHint: 'Usá el correo con el que te dieron de alta.',
    choosePassword: 'Elegí una contraseña',
    submit: 'Activar mi cuenta',
    backToLogin: 'Volver a iniciar sesión',
    sentTitle: 'Revisá tu correo',
    sentBody: 'Te enviamos un mensaje para confirmar la dirección. Confirmala y después iniciá sesión con la contraseña que elegiste.',
  },

forgot: {
    title: 'Recuperar contraseña',
    explanation: 'Escribí tu correo y te mandamos un enlace para elegir una contraseña nueva.',
    submit: 'Enviarme el enlace',
    sentTitle: 'Revisá tu correo',
    sentBody: 'Si esa dirección tiene una cuenta, te llega un enlace para cambiar la contraseña. Vence en una hora.',
    tooMany: 'Pediste el enlace varias veces seguidas. Esperá unos minutos y volvé a intentar.',
  },

  reset: {
    title: 'Elegí una contraseña nueva',
    newPassword: 'Contraseña nueva',
    submit: 'Guardar contraseña',
    doneBody: 'Listo, ya podés entrar con tu contraseña nueva.',
    expiredTitle: 'El enlace no es válido',
    expiredBody: 'Puede que haya vencido o que ya lo hayas usado. Pedí uno nuevo.',
    requestAnother: 'Pedir otro enlace',
  },

professionalSignup: {
    title: 'Postulación de profesional',
    submit: 'Enviar postulación',
    pickDiscipline: 'Elegí tu disciplina',
    disciplineHint: 'Define tu especialidad y la cuota que te corresponde.',
    licenseHint: 'Número de matrícula profesional.',
    feeTitle: 'Cuota de suscripción',
    feeNotSet: 'Todavía no hay una cuota definida para esta disciplina. El administrador te la va a confirmar.',
    annualSaving: 'Ahorrás {percent}% pagando por año',
    acceptFee: 'Entiendo y acepto abonar la cuota de {amount} por mes para usar el sistema.',
    mustAcceptFee: 'Necesitamos que aceptes la cuota para enviar la postulación.',
    approvalNotice: 'Tu postulación queda pendiente hasta que el administrador la revise. Te vamos a avisar por correo.',
    alreadyAdded: '¿El administrador ya te dio de alta?',
    sentTitle: 'Revisá tu correo',
    // Someone the administrator has already added lands here too, because the
    // form cannot know which of the two they are without disclosing whether an
    // address is on file. So it describes both outcomes instead of asserting
    // the wrong one — the claim on first sign-in settles which it is.
    sentBody: 'Confirmá la dirección y después iniciá sesión. Si el administrador ya te dio de alta, entrás directo. Si te estás postulando, vas a ver el estado de tu solicitud.',
    pendingTitle: 'Tu postulación está en revisión',
    pendingBody: 'El administrador todavía no revisó tu solicitud. Apenas la apruebe vas a poder entrar con este mismo usuario.',
    rejectedTitle: 'Tu postulación no fue aprobada',
    rejectedBody: 'El administrador no aprobó tu solicitud. Si creés que es un error, comunicate con el consultorio.',
  },

profile: {
    title: 'Mi perfil',
    contact: 'Mis datos',
    emailFixed: 'El correo identifica tu cuenta y no se cambia desde acá.',
    hours: 'Tus días y horarios',
    hoursHint: 'De acá sale la grilla que ves en Agenda semanal y la que ven tus pacientes al reservar.',
    hoursDay: 'Día',
    hoursOpen: 'Atiendo',
    hoursFrom: 'Desde',
    hoursTo: 'Hasta',
    hoursCopy: 'Copiar a los demás días',
    hoursCopied: 'Copiamos ese horario a los otros días que atendés.',
    hoursClosed: 'No atiendo',
    hoursNoneOpen: 'No marcaste ningún día. Así, nadie va a poder reservarte turnos.',
    hoursBadRange: 'En {day}, la hora de fin tiene que ser posterior a la de inicio.',
    hoursSlots: { one: '1 turno por día', other: '{count} turnos por día' },
    notifications: 'Avisos',
    calendarInvites: 'Recibir invitaciones de calendario',
    calendarInvitesHint: 'Te mandamos el turno por correo para que lo agregues a tu calendario. Podés desactivarlo cuando quieras.',
    reminders: 'Recibir recordatorio antes de la sesión',
    remindersHint: 'Un correo antes de cada sesión, para que no se te pase.',
    saved: 'Guardamos tus cambios.',
  },

  // A professional's own tabs in Configuración: Mis datos, Horarios and Avisos.
  mySettings: {
    nameRequired: 'Escribí tu nombre y tu apellido.',
    sessionLengthTitle: 'Duración de la sesión',
    sessionLengthHint: 'Se usa para los turnos que se agenden de ahora en más; los ya agendados mantienen su duración. Tus horarios, abajo, cuentan los turnos con este valor.',
    sessionLengthLabel: 'Cada sesión dura',
    sessionLengthInvalid: 'La duración tiene que ser un número entero de minutos entre 15 y 180.',
    hoursSaved: 'Guardamos tus horarios.',
    blocksPointer: '¿Un rato puntual sin turnos, como unas vacaciones? Se bloquea en Agenda semanal, debajo de la semana.',
    blocksLink: 'Ir a Agenda semanal',
    invitesTitle: 'Invitaciones de calendario',
    reminderTitle: 'Recordatorio a tus pacientes',
    reminderHint: 'Cuántas horas antes de cada turno les llega el correo. Vacío: el valor del consultorio, {hours} horas.',
    reminderHours: 'Se manda',
    reminderInvalid: 'El recordatorio tiene que ser un número entero de horas entre 1 y 168, o quedar vacío.',
  },

  // Configuración → Cobros: what a professional charges, and what their
  // patients see about it.
  charges: {
    loadFailed: 'No pudimos cargar tus coberturas y valores',
    privateFeeTitle: 'Valor particular',
    privateFeeHint: 'Para los pacientes particulares sin un valor acordado propio. Al guardarlo, se actualizan los turnos futuros que todavía no se cobraron.',
    privateFeeLabel: 'Por sesión',
    privateFeeInvalid: 'El valor particular tiene que ser un número mayor o igual a 0.',
    privateFeeSaved: 'Guardamos el valor y actualizamos los turnos futuros sin cobrar.',
    coveragesTitle: 'Coberturas que atendés',
    coveragesHint: 'Cuánto paga el paciente y cuánto te paga la obra social en cada una. Cada valor se guarda al salir del campo.',
    coveragesSaved: 'Guardamos los valores y actualizamos los turnos futuros sin cobrar.',
    addCoverage: 'Agregar cobertura que atiendo',
    noCoverages: 'Todavía no cargaste coberturas',
    noCoveragesHint: 'Sin coberturas cargadas, los turnos con obra social se agendan en $0.',
    stopAccepting: 'Dejar de atender esta cobertura',
    missingCoverage: '¿Falta una obra social o una subcategoría? Se agrega en Datos maestros.',
    goToMasterData: 'Ir a Datos maestros',
    termsTitle: 'Cuándo paga cada obra social',
    termsHint: 'Define si una deuda aparece como vencida. Los turnos ya agendados mantienen la fecha que se les calculó. Cada cambio se guarda solo.',
    noAttendedCompanies: 'Acá aparecen las obras sociales de las coberturas que atendés, y todavía no cargaste ninguna.',
    termKind: 'Plazo',
    termValue: 'Cantidad',
    termEndOfMonth: 'Último día del mes',
    termStartOfMonth: 'Primer día del mes',
    termDays: 'Días corridos',
    termDayOfMonth: 'Día fijo del mes',
    termDaysUnit: 'días después de la sesión',
    termMonthsUnit: 'mes/es después de la sesión',
    termDayUnit: 'de cada mes,',
    termDayOfMonthHint: 'Ej.: el 10, 2 meses después de la sesión. Si el mes no llega a ese día, se toma el último.',
    termSaved: 'Guardamos el plazo de pago.',
    cancellationTitle: 'Cancelaciones',
    cancellationHint: 'Se aplica cuando cancela el paciente: las cancelaciones que hacés vos nunca se cobran. En Agenda del día, «Cobrar igual» siempre decide un turno puntual.',
    cancellationNoticeHours: 'Se cobra si el paciente avisa con menos de',
    hoursOfNotice: 'horas de anticipación',
    cancellationNoticeHoursHint: 'Vacío: ninguna cancelación se cobra por sí sola. Con un valor, una ausencia se cobra siempre. A un paciente particular se le cobra su valor; a uno de obra social, el valor de abajo para esa obra social o, si no lo cargaste, tu valor particular.',
    cancellationNoticeInvalid: 'Las horas de aviso tienen que ser un número entero entre 1 y 720, o quedar vacías.',
    cancellationSaved: 'Guardamos tu política de cancelación.',
    cancellationFeesTitle: 'Valor por cancelación de cada obra social',
    cancellationFeesHint: 'Lo que paga un paciente de esa obra social por una cancelación con cargo. Vacío: tu valor particular. Cada valor se guarda al salir del campo.',
    cancellationFee: 'Valor por cancelación',
    cancellationFeeSaved: 'Guardamos el valor por cancelación. Se usa en las próximas cancelaciones con cargo.',
    cancellationFeeCleared: 'Quitamos el valor: las cancelaciones con cargo de esa obra social usan tu valor particular.',
    paymentsTitle: 'Pagos en la cuenta de tus pacientes',
    showsBalance: 'Mostrarles lo que te deben y lo que te pagaron',
    showsBalanceHint: 'Lo ven en Pagos, en su cuenta: las sesiones que ya pasaron y no pagaron, los pagos que registraste y los turnos ya pagos por adelantado. No ven el precio de los turnos que vienen ni lo que te paga la obra social. Mientras esté desactivado, ven que no mostrás los pagos en la app.',
    transferTitle: 'Datos para transferencias',
    transferHint: 'Tus pacientes los ven en su página de Pagos, con un botón para copiarlos.',
    paymentAlias: 'Alias o CBU/CVU',
    paymentAliasHint: 'Un alias (de 6 a 20 letras, números, puntos o guiones) o los 22 números del CBU o CVU.',
    paymentHolder: 'A nombre de',
    paymentAliasInvalid: 'El alias tiene que tener de 6 a 20 letras, números, puntos o guiones, o ser un CBU o CVU de 22 números.',
    paymentHolderRequired: 'Escribí a nombre de quién está la cuenta, para que el paciente pueda comprobar a quién le paga.',
    paymentsNotShown: 'Tus pacientes van a verlo cuando actives «Mostrarles lo que te deben y lo que te pagaron».',
    paymentsSaved: 'Guardamos lo que ven tus pacientes sobre pagos.',
    agreedPricesTitle: 'Valores acordados por paciente',
    agreedPricesHint: 'Pacientes con un valor distinto al de tu cobertura o a tu valor particular. Los que diste de baja no aparecen.',
    noAgreedPrices: 'Ningún paciente tiene un valor acordado distinto',
    clearPrice: 'Quitar el valor acordado',
    confirmClearPrice: '¿Quitar el valor acordado de {name}? Va a pasar a pagar el valor por defecto.',
    priceCleared: '{name} vuelve al valor por defecto.',
  },

  roles: {
    admin: 'Administrador',
    therapist: 'Profesional',
    patient: 'Paciente',
  },

  nav: {
    home: 'Inicio',
    agenda: 'Agenda del día',
    calendar: 'Calendario',
    patients: 'Pacientes',
    professionals: 'Profesionales',
    approvals: 'Solicitudes',
    payments: 'Pagos',
    cashFlow: 'Flujo de caja',
    clinicalHistory: 'Historia Clínica',
    locations: 'Sedes',
    masterData: 'Datos maestros',
    subscriptions: 'Suscripciones',
    settings: 'Configuración',
    checkIn: 'Check-in',
    menu: 'Menú',
    forms: 'Formularios',
  },

  // Assessment forms (0065): the professional's questionnaires, sending and
  // filling in from the clinical history, the patient's answers, and admin's
  // pasting of the standard forms' official wording.
  forms: {
    title: 'Formularios',
    subtitle: 'Tus cuestionarios y los formularios estándar. Se envían y se completan desde la historia clínica de cada paciente.',
    newForm: 'Nuevo cuestionario',
    own: 'Tus cuestionarios',
    ownNone: 'Todavía no escribiste ningún cuestionario.',
    standard: 'Formularios estándar',
    standardHint: 'Los ofrece el consultorio. El PHQ-9 se completa solo en sesión.',
    edit: 'Editar',
    archive: 'Archivar',
    restore: 'Volver a usar',
    archived: 'Archivado',
    notReady: 'Falta cargar el texto oficial',
    questionsCount: { one: '1 pregunta', other: '{count} preguntas' },
    newTitle: 'Nuevo cuestionario',
    editTitle: 'Editar cuestionario',
    editKeepsSent: 'Los cambios valen para los próximos envíos: lo que ya se respondió conserva las preguntas con las que se respondió.',
    formTitle: 'Título',
    titleRequired: 'Poné un título al formulario.',
    description: 'Descripción',
    descriptionHint: 'Opcional. El paciente la lee arriba de las preguntas.',
    inSessionOnly: 'Solo en sesión',
    inSessionOnlyHint: 'No se envía al paciente: lo completás vos durante la sesión.',
    questions: 'Preguntas',
    kindLabel: 'Tipo',
    kind: { scale: 'Escala', yes_no: 'Sí o no', text: 'Texto libre', choice: 'Opciones' },
    add: { scale: 'Agregar escala', yes_no: 'Agregar sí o no', text: 'Agregar texto libre', choice: 'Agregar opciones' },
    prompt: 'Pregunta {n}',
    scaleFrom: 'Desde',
    scaleTo: 'Hasta',
    labelFor: 'Etiqueta del {value}',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    removeQuestion: 'Quitar pregunta',
    bands: 'Rangos del puntaje',
    bandsHint: 'Opcional. Cada rango le pone nombre a un tramo del total, que va de 0 a {max}. Las respuestas de texto no suman.',
    bandFrom: 'Desde',
    bandTo: 'Hasta',
    bandLabel: 'Nombre',
    addBand: 'Agregar rango',
    removeBand: 'Quitar rango',
    // Options in the order they are printed, and subscales (0068).
    optionsHint: 'Las opciones se muestran en este orden, y cada una lleva su puntaje. Sirve para instrumentos donde el orden impreso no va de menor a mayor.',
    optionLabel: 'Opción {n}',
    optionScore: 'Puntaje',
    addOption: 'Agregar opción',
    removeOption: 'Quitar opción',
    scalesTitle: 'Subescalas',
    scalesHint: 'Opcional. Cada subescala suma solo las preguntas que le indiques, y puede tener sus propios rangos.',
    scaleName: 'Nombre de la subescala',
    scaleItems: 'Preguntas',
    scaleItemsHint: 'Números de pregunta separados por coma. Por ejemplo: 1, 6, 8, 10.',
    scaleMax: 'Suma hasta {max}.',
    addScale: 'Agregar subescala',
    removeScale: 'Quitar subescala',
    scaleBandsTitle: 'Rangos de esta subescala',
    scaleScore: '{label} {total}',
    scaleScoreBand: '{label} {total} · {band}',
    subscores: 'Subescalas',
    saved: 'Guardamos el cuestionario.',
    historyTitle: 'Formularios',
    historyHint: 'Enviá uno para que el paciente lo responda desde la app, o completalo con él en la sesión. Solo vos ves los resultados.',
    noneReady: 'No tenés formularios listos para usar. Podés escribir uno en Formularios.',
    pickForm: 'Formulario',
    send: 'Enviar al paciente',
    fillInSession: 'Completar en sesión',
    sent: 'Enviamos {title}. Le aparece al paciente en Inicio.',
    recordTitle: '{title} · {name}',
    record: 'Guardar respuestas',
    recorded: 'Guardamos {title}: {score}.',
    missingAnswer: 'Falta responder la pregunta {n}.',
    noResults: 'Todavía no hay formularios para este paciente.',
    score: 'Puntaje {total}',
    status: { pending: 'Esperando respuesta', answered: 'Respondido', withdrawn: 'Retirado' },
    filledInSession: 'completado en sesión',
    filledByPatient: 'respondido por el paciente',
    sentToPatient: 'enviado al paciente',
    withdraw: 'Retirar',
    viewAnswers: 'Ver respuestas',
    noAnswer: 'Sin respuesta',
    pendingTitle: 'Formularios para completar',
    pendingHint: 'Tu profesional te pidió que los respondas. Solo tu profesional ve tus respuestas.',
    pendingLine: '{title} · de {professional}',
    forPerson: 'Para {name}',
    answer: 'Responder',
    submit: 'Enviar respuestas',
    submitted: 'Enviamos tus respuestas a {professional}.',
    standardTextTitle: 'Texto de los formularios estándar',
    standardTextHint: 'El PHQ-9 y el GAD-7 se usan con su redacción oficial en castellano. Copiala una vez de la fuente oficial: hasta entonces no se pueden enviar ni completar.',
    standardReady: 'Listo para usar',
    standardMissing: 'Falta el texto',
    standardDescription: 'Instrucción que lee el paciente',
    standardPrompt: 'Pregunta {n}',
    standardLabels: 'Opciones de respuesta, las mismas para todas las preguntas',
    standardLabelFor: 'Opción {value}',
    saveText: 'Guardar texto',
    textSaved: 'Guardamos el texto de {title}.',
  },

  insurance: {
    company: 'Obra Social / Prepaga',
    companyPlural: 'Obras Sociales / Prepagas',
    plan: 'Subcategoría',
    planPlural: 'Subcategorías',
    particular: 'Particular',
    billingMode: 'Forma de cobro',
    billingModes: {
      plan: 'Prestador',
      particular: 'Particular',
    },
    billingModeHints: {
      plan: 'Le facturás a la obra social; el paciente paga el copago.',
      particular: 'El paciente te paga tu valor. La obra social queda solo como dato.',
    },
    coverage: 'Cobertura',
    addCompany: 'Agregar Obra Social / Prepaga',
    addPlan: 'Agregar subcategoría',
    accepted: 'Coberturas que atiendo',
    patientCopay: 'Copago del paciente',
    planReimbursement: 'Pago de la obra social',
    privateFee: 'Valor particular',
    defaultPrivateFee: 'Valor particular por defecto',
    requestRemoval: 'Solicitar eliminación',
    removalRequested: 'Eliminación solicitada',
    onlyAdminDeletes: 'Solo el administrador puede eliminar datos maestros',
  },

  specializations: {
    discipline: 'Disciplina',
    disciplinePlural: 'Disciplinas',
    specialization: 'Especialidad',
    specializationPlural: 'Especialidades',
    primaryDiscipline: 'Disciplina principal',
    addDiscipline: 'Agregar disciplina',
    addSpecialization: 'Agregar especialidad',
  },

  appointments: {
    title: 'Turnos',
    appointment: 'Turno',
    book: 'Reservar turno',
    reschedule: 'Reprogramar',
    cancelAppointment: 'Cancelar turno',
    patient: 'Paciente',
    professional: 'Profesional',
    location: 'Sede',
    duration: 'Duración',
    recurrence: 'Recurrencia',
    recurringSeries: 'Serie de turnos',
    repeatWeekly: 'Repetir semanalmente',
    repeatFor: 'Repetir durante {count} semanas',
    noAppointmentsToday: 'No hay turnos para hoy',
    noUpcoming: 'No tenés turnos próximos',
    previous: 'Anterior',
    current: 'En curso',
    upcoming: 'Próximos',
    status: {
      requested: 'Pendiente de aprobación',
      scheduled: 'Agendado',
      waiting: 'En sala de espera',
      in_session: 'En sesión',
      completed: 'Finalizado',
      cancelled: 'Cancelado',
      no_show: 'No asistió',
    },
    cancellationWarning: {
      one: 'Este paciente canceló o faltó a {count} turno seguido',
      other: 'Este paciente canceló o faltó a {count} turnos seguidos',
    },
    // What became of a session asked for in hours that need approval (0055).
    requestOutcome: {
      declined: 'No confirmado',
      lapsed: 'Pedido vencido',
      withdrawn: 'Pedido retirado',
    },
    answerRequest: 'Responder en Solicitudes',
  },

agenda: {
    today: 'Hoy',
    inSession: 'En sesión',
    waitingRoom: 'En sala de espera',
    finished: 'Ya atendidos',
    markArrived: 'Llegó',
    startSession: 'Iniciar sesión',
    endSession: 'Finalizar',
    chargeAnyway: 'Cobrar igual',
    allProfessionals: 'Ver todos los profesionales',
    inOfficeSince: 'En consultorio desde las {time}',
    notCheckedIn: 'No registraste tu entrada al consultorio',
    cancelBody: 'El turno se marca como cancelado y no se cobra. Si corresponde cobrarlo, después podés marcarlo con "Cobrar igual".',
    cancelReason: 'Motivo (opcional)',
    cancelReasonPlaceholder: 'Por ejemplo: avisó con anticipación',
  },

booking: {
    title: 'Agenda semanal',
    titlePatient: 'Reservar turno',
    subtitlePatient: 'Elegí un horario disponible con tu profesional.',
    subtitleProfessional: 'Tu semana completa. Tocá un horario libre para agendar.',
    forPatient: '¿Para quién es el turno?',
    customTime: 'Agendar en otro horario',
    customTimeHint: 'Para un turno que no cae en la grilla: la grilla arranca a tu hora de inicio y avanza de a una sesión.',
    customTimeLabel: 'Día y hora',
    customTimeInvalid: 'Elegí un día y una hora.',
    previousWeek: 'Semana anterior',
    nextWeek: 'Semana siguiente',
    noSlots: 'Sin horarios',
    confirmTitle: 'Confirmar turno',
    confirm: 'Confirmar',
    bookedTitle: 'Turno agendado',
    requestSentTitle: 'Pedido enviado',
    requestSentBody: '{professional} tiene que confirmarlo. Te avisamos por correo cuando responda.',
    yourProfessional: 'Tu profesional',
    needsApproval: 'Este horario lo confirma tu profesional: el turno queda pedido hasta que responda, y te avisamos por correo. Si no responde a tiempo, el pedido se cancela solo.',
    bookedCount: { one: 'Se agendó 1 turno.', other: 'Se agendaron {count} turnos.' },
    skippedTitle: 'No pudimos agendar estas fechas porque el horario ya estaba ocupado:',
    repeatLabel: 'Repetir este turno',
    repeatHint: 'Para pacientes con horario fijo. Acordalo antes con el paciente.',
    repeatOn: 'Repetirlo en el tiempo',
    repeatDays: 'En estos días',
    repeatFixedDay: 'El día del turno que elegiste: la serie siempre lo incluye.',
    repeatEvery: 'Cada',
    repeatUntil: 'Hasta el',
    repeatCount: { one: 'Se agenda 1 turno.', other: 'Se agendan {count} turnos.' },
    repeatUntilRequired: 'Elegí hasta cuándo se repite.',
    repeatNoDates: 'Con esos días y esa fecha no hay ningún turno para agendar.',
    repeatTooMany: 'Son demasiados turnos para una sola serie: acortá la fecha de fin.',
    repeatNotice: 'Si alguna fecha está ocupada, la salteamos y te avisamos cuáles quedaron afuera.',
    weeks: { one: 'semana', other: 'semanas' },
    noPatients: 'Todavía no tenés pacientes aprobados',
    noApprovedPatient: 'Todavía no podés reservar turnos',
    noApprovedPatientHint: 'Tu profesional tiene que aprobar la solicitud antes de que puedas reservar.',
  },

  payments: {
    title: 'Pagos',
    patientPayment: 'Pago del paciente',
    patientPaymentDate: 'Fecha de pago del paciente',
    planPayment: 'Pago de la obra social',
    planPaymentDate: 'Fecha de pago de la obra social',
    value: 'Valor',
    method: 'Medio de pago',
    methods: {
      cash: 'Efectivo',
      transfer: 'Transferencia',
      card: 'Tarjeta',
      other: 'Otro',
    },
    status: {
      pending: 'Pendiente',
      partial: 'Parcial',
      paid: 'Pagado',
      waived: 'Bonificado',
      not_applicable: 'No aplica',
      overdue: 'Vencido',
    },
    registerPayment: 'Registrar pago',
    sessionDate: 'Fecha del turno',
    collectedThisMonth: 'Cobrado este mes',
    collectedSplit: 'Paciente {patient} · Obra social {plan}',
    collectedUnassigned: 'Sin asignar a turnos: {amount}',
    collectedForSessions: 'De sesiones de este mes',
    collectedForSessionsHint: 'Del paciente y de la obra social, por los turnos de este mes.',
    collectedDiffers: 'No tiene por qué coincidir con lo cobrado: parte de lo cobrado puede estar a favor, todavía sin turno, y un turno de este mes puede haberse pagado con plata que entró otro mes.',
    billable: 'Facturable',
    subtitle: 'Qué te pagó cada paciente y qué te debe cada obra social, turno por turno.',
    amount: 'Importe',
    paidAt: 'Fecha de pago',
    registered: 'Pago registrado',
    reachedList: 'Se aplicó a {list}.',
    reachedItem: '{day} ({amount})',
    reachedItemPartial: '{day} ({amount} de {price}, queda parcial)',
    reachedNone: 'No había turnos por cobrar: queda todo a favor.',
    creditMoney: 'Quedan {amount} a favor para los próximos turnos.',
    creditLine: '{amount} a favor, de un pago de {paid} del {day}',
    partialLeft: 'Faltan {amount}',
    waive: 'Bonificar este turno',
    unwaive: 'Quitar la bonificación',
    allocated: 'Aplicado a turnos',
    inCredit: 'A favor',
    correctExplanation: 'Al corregir el importe, el pago se vuelve a aplicar desde la deuda más vieja.',
    creditsTitle: 'Saldos a favor',
    applyExplanation: 'El pago va primero a los turnos ya realizados que se deben, del más viejo al más nuevo, y después a los agendados. Si no alcanza para un turno, ese turno queda parcial. Si sobra, queda a favor y se usa en los próximos turnos.',
    patientOwes: 'Te debe el paciente',
    planOwes: 'Te debe la obra social',
    notBillable: 'No se cobra',
    nothingToShow: 'No hay turnos para mostrar',
    filterPending: 'Con saldo pendiente',
    filterPaid: 'Cobrados',
    invoiceOnly: 'Solo los que requieren factura',
    invoiceBadge: 'Factura',
    markPlanPaid: 'Marcar pago de la obra social',
    whoOwesTitle: 'Quién te debe',
    whoOwesSubtitle: 'Total pendiente de cobro: {amount}. Incluye turnos de cualquier mes.',
    unpaidSessions: 'Turnos con saldo',
    oldestUnpaid: 'El más antiguo',
    totalOwed: 'Total adeudado',
    daysAgo: { one: 'hace 1 día', other: 'hace {count} días' },
    monthScope: 'Los turnos de abajo son de este mes',
    planOverdue: '{amount} ya vencido',
    planAllOnTime: 'Todavía dentro del plazo',
    overdue: 'Vencido',
    expected: 'Vence',
    owedTo: 'Le debe a',
    registeredTitle: 'Pagos registrados',
    registeredSubtitle: 'Pagos cargados en este mes. Podés corregirlos o anularlos.',
    editPayment: 'Corregir pago',
    voidPayment: 'Anular pago',
    voidReasonPrompt: 'Anular el pago de {name}: la plata que había llegado a esos turnos vuelve a faltar, y el pago queda registrado como anulado.\n\n¿Por qué se anula?',
    voidReasonRequired: 'Para anular un pago hay que decir por qué.',
  },

  // What a professional bills for a month (0053).
  billingSummary: {
    open: 'Resumen de facturación',
    title: 'Resumen de facturación',
    subtitle: 'Lo que facturás a cada obra social y a los pacientes con factura, mes por mes. El primer día hábil de cada mes te llega por correo el del mes anterior.',
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    unclosed: {
      one: 'Quedó 1 turno de este mes sin cerrar (sigue como agendado) y no entra en el resumen. Marcalo como finalizado, cancelado o ausente en Agenda del día.',
      other: 'Quedaron {count} turnos de este mes sin cerrar (siguen como agendados) y no entran en el resumen. Marcalos como finalizados, cancelados o ausentes en Agenda del día.',
    },
    empty: 'No hay sesiones para facturar este mes.',
    coverageSubtitle: {
      one: '1 sesión realizada · {hours} · a la obra social {amount}',
      other: '{count} sesiones realizadas · {hours} · a la obra social {amount}',
    },
    invoicedTitle: 'Pacientes con factura',
    invoicedSubtitle: 'Los pacientes marcados con factura: sus sesiones realizadas y las cancelaciones con cargo.',
    columns: {
      patient: 'Paciente',
      idNumber: 'DNI',
      plan: 'Subcategoría',
      coverage: 'Cobertura',
      held: 'Realizadas',
      dates: 'Fechas',
      byPlan: 'Obra social',
      copays: 'Copagos',
      heldAmount: 'Sesiones',
      charged: 'Canceladas con cargo',
      uncharged: 'Canceladas sin cargo',
      toInvoice: 'A facturar',
    },
  },

  // Cancelling a session, as the professional records it (0052).
  cancellation: {
    who: '¿Quién cancela?',
    byPatient: 'El paciente',
    byTherapist: 'Yo',
    noticeAt: '¿Cuándo avisó?',
    noticeAtHint: 'Si te avisó antes, por ejemplo por teléfono ayer, poné ese momento: de eso depende si se cobra.',
    noticeInvalid: 'El aviso tiene que ser una fecha y hora que ya pasó.',
    byYou: 'Cancelás vos: no se cobra.',
    noPolicy: 'No tenés política de cancelación: no se cobra. Si corresponde, marcá «Cobrar igual» en Agenda del día.',
    charged: 'Avisó con {hours} h de anticipación y tu política pide {policy} h: se cobra.',
    notCharged: 'Avisó con {hours} h de anticipación y tu política pide {policy} h: no se cobra.',
    paidMoves: 'Estaba pago: el pago pasa a la sesión más antigua que se deba, si no hay al próximo turno, y si tampoco queda como saldo a favor.',
  },

  // A patient cancelling their own session, from Inicio or Reservar turno (0057).
  patientCancel: {
    title: 'Cancelar turno',
    withdrawTitle: 'Retirar pedido de turno',
    confirm: 'Cancelar turno',
    withdraw: 'Retirar pedido',
    rowCancel: 'Cancelar',
    rowWithdraw: 'Retirar pedido',
    listHint: 'Tocá un turno para cancelarlo.',
    legend: 'Tus turnos aparecen en verde y tus pedidos en ámbar, con el nombre de quien va. Tocá uno para cancelarlo.',
    gridHint: 'Turno de {name}: tocá para cancelarlo',
    gridWithdrawHint: 'Pedido de {name}: tocá para retirarlo',
    hoursLeft: {
      one: 'Falta 1 h y tu profesional pide {policy} hs de aviso:',
      other: 'Faltan {count} h y tu profesional pide {policy} hs de aviso:',
    },
    lessThanAnHour: 'Falta menos de 1 h y tu profesional pide {policy} hs de aviso:',
    charged: 'deberá cobrar la consulta igualmente. Cualquier cosa, hablá con tu profesional.',
    chargedAmount: 'deberá cobrar la consulta igualmente ({amount}). Cualquier cosa, hablá con tu profesional.',
    free: 'no tiene costo.',
    requestFree: 'Todavía no está confirmado: retirarlo no tiene costo.',
    reasonPlaceholder: 'Lo va a ver tu profesional',
    tellsProfessional: 'Le avisamos por correo a {professional}.',
    cancelled: 'Cancelamos el turno del {when}.',
    withdrawn: 'Retiramos el pedido de turno del {when}.',
  },

  // The patient's own reading of Pagos (0046).
  myPayments: {
    title: 'Pagos',
    subtitle: 'Lo que debés y lo que pagaste, con cada profesional.',
    none: 'Todavía no te atendés con ningún profesional.',
    notShown: '{professional} no muestra los pagos en la app. Si tenés dudas, consultale directamente.',
    owes: 'Sin pagar',
    upToDate: 'Estás al día',
    owedTitle: 'Sesiones sin pagar',
    kinds: {
      completed: 'Sesión',
      cancelled: 'Cancelación con cargo',
      no_show: 'Ausencia con cargo',
    },
    prepaidTitle: 'Pagado por adelantado',
    credit: 'Tenés {amount} a favor para los próximos turnos.',
    partial: 'pagaste una parte de {price}',
    partialAhead: 'pago parcial',
    paymentsTitle: 'Pagos registrados',
    noPayments: 'Todavía no hay pagos registrados.',
    // Where to transfer, from a professional who shows payments (0062).
    transferTitle: 'Para transferir',
    transferHolder: 'A nombre de {holder}',
    copy: 'Copiar',
    copied: 'Copiado',
    hint: 'Solo figuran las sesiones que ya pasaron y los pagos que registró tu profesional. Si algo no coincide con lo que pagaste, hablalo con tu profesional.',
  },

  // Links de pago on the professional's own Mercado Pago (0074).
  mercadopago: {
    title: 'Mercado Pago',
    hint: 'Links de pago que tus pacientes pagan con Mercado Pago. La plata va directo a tu cuenta, y el pago se registra solo en Cobros.',
    connect: 'Conectar Mercado Pago',
    connectHint: 'Te lleva a Mercado Pago para que autorices a la app. No escribís ninguna clave acá.',
    connected: 'Conectado',
    since: 'Desde el {date}',
    testAccount: 'cuenta de prueba',
    disconnect: 'Desconectar',
    confirmDisconnect: '¿Desconectar tu Mercado Pago? Los links que mandaste y todavía no se pagaron dejan de registrarse solos.',
    connectedNow: 'Conectamos tu Mercado Pago. Ya podés mandar links de pago desde Cobros.',
    connectFailed: 'No se pudo conectar Mercado Pago',
    linkButton: 'Link de pago',
    linkTitle: 'Link de pago',
    linkHint: 'Se paga con Mercado Pago y la plata va a tu cuenta. Cuando el paciente pague, el pago se registra solo y va a la deuda más vieja.',
    linkAmount: 'Importe',
    createLink: 'Crear link',
    linkReady: 'Link listo para mandar',
    copy: 'Copiar',
    copied: 'Copiado',
    whatsapp: 'Mandar por WhatsApp',
    whatsappText: 'Hola, te paso el link para pagar las sesiones: {url}',
    payButton: 'Pagar con Mercado Pago',
    payHint: 'Te lleva a Mercado Pago. El pago va directo a {professional}.',
    returnedApproved: 'Recibimos tu pago. Puede tardar unos minutos en aparecer acá.',
    returnedPending: 'Tu pago está en proceso. Aparece acá cuando Mercado Pago lo confirme.',
  },

  cashFlow: {
    title: 'Flujo de caja',
    received: 'Cobrado',
    owed: 'Adeudado',
    collectedThisYear: 'Cobrado en {year}',
    receivedAll: 'Cobrado en total',
    unassigned: 'Sin asignar a turnos: {amount}',
    unassignedHint: 'Plata que ya cobraste y todavía no cubre ningún turno.',
    receivedThisMonth: 'Cobrado en el mes',
    owedThisMonth: 'Adeudado del mes',
    byCoverage: 'Por cobertura',
    byProfessional: 'Por profesional',
    month: 'Mes',
    subtitle: 'Lo cobrado se cuenta el mes que entró la plata, esté asignado a un turno o no; lo adeudado, el mes de la sesión.',
    wholePractice: 'Todo el consultorio',
    noData: 'Todavía no hay movimientos',
    loadFailed: 'No pudimos cargar tus movimientos',
  },

  locations: {
    title: 'Sedes',
    location: 'Sede',
    address: 'Dirección',
    online: 'Online',
    isOnline: 'Atención online',
    active: 'Activa',
    qrCode: 'Código QR',
    printQr: 'Imprimir QR',
    downloadQr: 'Descargar QR',
    qrHelp: 'Colocá este código en la entrada. Los pacientes lo escanean al llegar.',
    addLocation: 'Agregar sede',
    editLocation: 'Editar sede',
    saved: 'Guardamos la sede.',
    subtitle: 'Dónde atiende el consultorio. Cada sede física tiene su propio código QR.',
    none: 'Todavía no cargaste sedes',
    addressHint: 'Se muestra en el cartel que imprimís con el QR y en la invitación de calendario del turno.',
    posterHint: 'Escaneá el código para avisar que llegaste',
  },

  adoption: {
    title: 'Acceso de los pacientes',
    subtitle: 'Cuántos pueden entrar a su cuenta y ver sus turnos.',
    none: 'Todavía no cargaste pacientes',
    ofTotal: '{percent}% de {total}',
    hint: 'Los que no tienen correo no pueden crear su cuenta. Cargáselo desde Pacientes.',
  },

  reschedule: {
    newDay: 'Nuevo día',
    pick: 'Elegí el horario nuevo:',
    noSlots: 'Ese día no te queda ningún horario libre. Probá otro.',
    keepsPrice: 'Se mantiene el valor acordado y le llega la actualización del turno al calendario, no una cancelación.',
    done: 'Movimos el turno.',
  },

  slots: {
    free: 'Libre',
    freeToConfirm: 'Libre, con aprobación del profesional',
    toConfirm: 'A confirmar',
    takenBy: 'Ocupado: {who}',
    taken: 'Ocupado',
    blockedBy: 'Bloqueado: {who}',
    blocked: 'Bloqueado',
    legend: 'Verde: turno agendado. Gris: bloqueado por vos. Violeta: libre, con dónde es debajo. Ámbar: horario que necesita tu aprobación, y los pedidos esperando respuesta.',
  },

  availability: {
    hint: 'Cada horario es un rato en una sede. Si en ese rato también atendés online, marcalo: el paciente elige al reservar, y un turno ocupa ese horario para las dos cosas. Dos horarios del mismo día no se pueden superponer, porque estás en un solo lugar a la vez; sí pueden ir uno detrás del otro en sedes distintas.',
    addBlock: 'Agregar horario',
    removeBlock: 'Quitar este horario',
    closed: 'No atendés',
    place: 'Dónde',
    onlineOnly: 'Solo online',
    alsoOnline: 'También online',
    requiresApproval: 'Necesita tu aprobación',
    online: 'Online',
    inPerson: 'Presencial',
    sedeOrOnline: '{sede} u online',
    inactiveSede: 'Sede inactiva',
    sessions: { one: '1 turno', other: '{count} turnos' },
    copyDay: 'Copiar a los demás días con horarios',
    copied: 'Copiamos esos horarios a los demás días que tenían horarios.',
    noSedes: 'No hay sedes presenciales activas. Pedile a la administración que cargue una, o atendé solo online.',
    problems: {
      missingTimes: 'El {day} hay un horario sin hora de inicio o de fin.',
      endsBeforeStart: 'El {day} hay un horario que termina antes de empezar.',
      nowhere: 'El {day} hay un horario sin sede y sin online.',
      overlap: 'El {day} hay dos horarios que se superponen: estás en un solo lugar a la vez.',
    },
  },

  modality: {
    change: 'Cambiar modalidad',
    changeMany: 'Cambiar la modalidad de varios turnos',
    title: 'Cambiar modalidad · {name}',
    which: 'Qué turnos',
    one: 'Solo este turno',
    next: 'Los próximos',
    nextUnit: 'turnos',
    weeks: 'Las semanas del',
    weeksTo: 'al',
    to: 'Pasarlos a',
    sedeByHours: 'La sede de tus horarios en ese momento',
    count: { one: 'Cambia 1 turno:', other: 'Cambian {count} turnos:' },
    more: { one: 'y 1 más', other: 'y {count} más' },
    none: 'No hay turnos por delante en ese rango.',
    invitations: 'A quien ya recibió la invitación de calendario de un turno le llega la actualización con el lugar nuevo.',
    apply: 'Cambiar',
    changed: { one: 'Cambiamos 1 turno.', other: 'Cambiamos {count} turnos.' },
    unchanged: { one: '1 ya estaba así.', other: '{count} ya estaban así.' },
    skippedStarted: { one: '1 ya empezó o el paciente ya llegó, y queda como está.', other: '{count} ya empezaron o el paciente ya llegó, y quedan como están.' },
    skippedNoSede: { one: '1 quedó sin cambiar porque en ese momento no tenés horario presencial: elegí la sede y volvé a intentar.', other: '{count} quedaron sin cambiar porque en ese momento no tenés horario presencial: elegí la sede y volvé a intentar.' },
  },

  blocks: {
    title: 'Horarios bloqueados',
    subtitle: 'Ratos en los que no querés que te reserven turnos.',
    add: 'Bloquear horario',
    none: 'No tenés nada bloqueado esta semana',
    from: 'Desde',
    to: 'Hasta',
    allDay: 'Todo el día',
    reason: 'Motivo',
    reasonPlaceholder: 'Opcional. Solo lo ves vos.',
    kind: 'Tipo',
    kinds: {
      personal: 'Personal',
      vacation: 'Vacaciones',
      meeting: 'Reunión',
      blocked: 'Otro',
    },
    saved: 'Listo, ese rato queda bloqueado.',
    removed: 'Desbloqueamos ese rato.',
    confirmRemove: '¿Desbloquear este rato?',
    endBeforeStart: 'El final tiene que ser posterior al inicio.',
    overlapsAppointments: {
      one: 'Ojo: ya tenés 1 turno agendado en ese rato. Bloquearlo no lo cancela — si querés darlo de baja, hacelo desde la agenda.',
      other: 'Ojo: ya tenés {count} turnos agendados en ese rato. Bloquearlo no los cancela — si querés darlos de baja, hacelo desde la agenda.',
    },
    spansDays: 'Del {from} al {to}',
  },

  checkIn: {
    title: 'Check-in',
    arrived: 'Ya llegué',
    checkedIn: 'Check-in registrado',
    checkInAt: 'Hora de llegada',
    noAppointmentToday: 'No tenés turnos hoy en esta sede',
    tooEarly: 'Todavía es muy temprano para hacer el check-in',
    tooLate: 'El horario de check-in para este turno ya pasó',
    professionalCheckIn: 'Registrar entrada',
    professionalCheckOut: 'Registrar salida',
    waitingTime: 'Tiempo de espera',
    sessionDuration: 'Duración de la sesión',
    timeInOffice: 'Tiempo en consultorio',
    unknownCode: 'Código no válido',
    unknownCodeBody: 'Este código de check-in no existe o la sede está desactivada. Avisale a la recepción.',
    yourAppointmentsToday: 'Tus turnos de hoy',
    noAppointmentToday: 'No tenés turnos hoy en esta sede.',
    alreadyHere: 'Ya avisaste que llegaste',
    alreadyCheckedIn: 'Ya habías registrado tu llegada.',
    checkedInBody: 'Le avisamos a tu profesional. Tomá asiento, ya te van a llamar.',
    onlineNoCheckIn: 'Turno online, no hace falta check-in',
    otherLocation: 'Este turno es en otra sede',
    opensAt: 'Vas a poder avisar que llegaste a partir de las {time}',
    professionalSection: 'Tu jornada',
    attendanceStarted: 'Registramos tu entrada a las {time}.',
    attendanceEnded: 'Registramos tu salida a las {time}.',
  },

  clinicalHistory: {
    title: 'Historia Clínica',
    entry: 'Evolución',
    newEntry: 'Nueva evolución',
    sessionNotes: 'Notas de la sesión',
    patientAgeAtSession: 'Edad del paciente en la sesión',
    yearsOld: '{age} años',
    editHistory: 'Historial de ediciones',
    revisionCount: {
      one: '{count} versión anterior',
      other: '{count} versiones anteriores',
    },
    lastEdited: 'Última edición: {date}',
    privateNotice: 'Solo vos podés ver esta historia clínica.',
    pendingNote: 'Falta la evolución',
    written: 'Evolución cargada',
    noNoteYet: 'Todavía no cargaste la evolución de esta sesión.',
    noSessions: 'Este paciente todavía no tiene sesiones finalizadas',
    sessionCount: { one: '1 sesión registrada', other: '{count} sesiones registradas' },
    pendingTitle: 'Evoluciones pendientes',
    pendingBody: { one: 'Hay 1 sesión finalizada sin evolución cargada.', other: 'Hay {count} sesiones finalizadas sin evolución cargada.' },
    emptyNote: 'Escribí algo antes de guardar.',
    placeholder: 'Cómo se desarrolló la sesión, lo trabajado, objetivos, tareas para la próxima...',
    adminNoticeTitle: 'Historias clínicas',
    adminNotice: 'Cada historia clínica es privada del profesional que la escribió. Como administrador podés ver si falta cargarla, pero no su contenido.',
  },

  patients: {
    title: 'Pacientes',
    patient: 'Paciente',
    dependents: 'Grupo familiar',
    addDependent: 'Agregar familiar',
    relationship: 'Vínculo',
    self: 'Titular',
    dateOfBirth: 'Fecha de nacimiento',
    idNumber: 'DNI',
    approve: 'Aprobar',
    decline: 'Rechazar',
    pendingApproval: 'Pendiente de aprobación',
    assignedProfessional: 'Profesional asignado',
    noProfessional: 'Sin profesional asignado',
    noDependents: 'Todavía no agregaste a nadie',
    active: 'Activo',
    declined: 'Solicitud rechazada',
    block: 'Bloquear',
    unblock: 'Desbloquear',
    blocked: 'Bloqueado',
    count: { one: '1 paciente', other: '{count} pacientes' },
    none: 'No hay pacientes para mostrar',
    searchPlaceholder: 'Buscar por nombre o correo',
    editCoverage: 'Datos del paciente',
    contactSection: 'Contacto',
    emailAddHint: 'Con este correo va a poder registrarse y quedarse con su ficha, historia clínica incluida.',
    emailClaimedHint: 'Ya accede a su cuenta. Desde acá no se cambia: el correo identifica su acceso.',
    emailSaved: 'Guardamos el correo.',
    access: {
      none: 'Falta el correo',
      noneHint: 'Sin correo no puede crear su cuenta. Cargalo con el lápiz.',
      invited: 'Puede registrarse',
      invitedHint: 'Decile que entre a gestionsrlo.com y se registre con ese correo.',
      active: 'Tiene acceso',
      activeHint: 'Ya entra a su cuenta y ve sus turnos.',
      own: 'Sin acceso propio',
      ownHint: 'Es mayor de edad y está en la cuenta de otra persona, que ya no ve sus turnos. Cargale su propio correo con el lápiz.',
    },
    emailOwnHint: 'Es mayor de edad y está en la cuenta de otra persona. Con su propio correo le creamos una cuenta, que toma cuando se registra.',
    upcoming: 'Próximos turnos',
    upcomingNone: 'No tiene turnos próximos',
    upcomingCount: { one: '1 turno', other: '{count} turnos' },
    accessFilter: 'Acceso',
    accessAll: 'Todos',
    accessPending: 'Todavía sin cuenta',
    coverageSaved: 'Guardamos la cobertura. Los turnos futuros sin cobrar se actualizaron.',
    pickPlan: 'Elegí la subcategoría de la cobertura.',
    pickCompany: 'Elegí la obra social y la subcategoría.',
    coverageOnFile: 'Obra social (opcional)',
    coverageOnFileHint: 'Queda registrada aunque te pague particular.',
    noCoverageOnFile: 'Sin obra social',
    needsInvoice: 'Requiere factura',
    needsInvoiceHint: 'Marcalo si a este paciente le hacés factura.',
    detailsSaved: 'Guardamos los datos del paciente.',
    usesDefault: 'Usa tu valor particular por defecto',
    usesCopay: 'Copago de la cobertura',
    feeHint: 'Dejalo vacío para usar tu valor por defecto ({amount}).',
    repriceNotice: 'Los turnos ya realizados o cobrados no cambian. Solo se actualizan los turnos futuros que todavía no se cobraron.',
    // Deleting a record loaded by mistake (0062). The per-patient block that
    // had its texts here became Dar de baja (0064).
    deleteRecord: 'Borrar ficha cargada por error',
    confirmDelete: '¿Borrar la ficha de {name}? No se puede deshacer.',
    deleted: 'Borramos la ficha de {name}.',
    cannotDelete: 'Esta ficha no se puede borrar: dala de baja.',
    refer: 'Derivar a otro profesional',
    referTitle: 'Derivar a {name}',
    referExplanation: 'El profesional que elijas recibe un pedido y decide si lo acepta. Va a ver los datos personales del paciente y el motivo que escribas acá; no ve tus turnos, tus cobros ni tu historia clínica. Por confidencialidad no te mostramos con qué otros profesionales se atiende: si ya se atiende con el que elijas, o ya se lo pidió, no se crea otro pedido y nadie recibe aviso. Si no lo sabés, preguntale al paciente.',
    referProfessional: 'Profesional',
    referPick: 'Elegí un profesional',
    referNote: 'Motivo de la derivación (opcional)',
    referNoteHint: 'Lo leen el profesional que la recibe y también el paciente.',
    referAgreed: 'El paciente está de acuerdo con la derivación',
    referAgreedHint: 'Sin su acuerdo no se deriva. Queda registrado el momento en que lo confirmaste.',
    referAgreeRequired: 'Confirmá que el paciente está de acuerdo con la derivación.',
    referSend: 'Enviar derivación',
    referSent: 'Listo. Si {name} todavía no se atendía con {professional} ni se lo había pedido, {professional} ya tiene tu derivación para aceptarla. Si ya se atendía o ya se lo había pedido, no cambió nada. Por confidencialidad no podemos decirte cuál de los dos casos es.',
    pendingRequest: 'Pidió atenderse con vos',
    answerRequest: 'Responder',
    status: { approved: 'Activo', pending: 'Pendiente', declined: 'Rechazado', revoked: 'Dado de baja' },
    addPatient: 'Agregar paciente',
    addExplanation: 'Cargás vos al paciente y queda listo para agendar. No necesita registrarse: si más adelante querés darle acceso, se registra con el mismo correo y toma esta ficha.',
    created: 'Paciente agregado.',
    dobHint: 'La usamos para registrar la edad en cada sesión de la historia clínica.',
    emailHint: 'Opcional. Sin correo no va a poder acceder a su cuenta ni recibir avisos.',
    coverageOptionalHint: 'Si no elegís ninguna, queda como Particular.',
    agreedFee: 'Valor acordado',
    agreedFeeHint: 'Dejalo vacío para usar tu valor particular por defecto.',
    agreedFeePlanHint: 'Dejalo vacío para usar el copago de esa cobertura.',
    discharge: 'Dar de alta / baja',
    dischargePrompt: '¿Por qué dejás de atender a {name}? (opcional). Su historia clínica se conserva, y si se atiende con otros profesionales, sigue con ellos.',
    discharged: 'Diste de baja a {name}. Su historia clínica se conserva.',
    dischargeFutureConfirm: '{name} tiene {count} turno(s) por delante. ¿Los cancelamos y le das de baja? Se le avisa por calendario.\n\nSi preferís resolverlos a mano desde la agenda, elegí Cancelar.',
    dischargedWithCancellations: 'Diste de baja a {name} y se cancelaron {count} turno(s). Su historia clínica se conserva.',
    readmit: 'Volver a atender',
    readmitted: '{name} vuelve a estar activo.',
    showDischarged: 'Ver dados de baja ({count})',
    sessionValue: 'Valor por sesión',
    agreedValue: 'Valor acordado',
    noFeeSet: 'Sin valor definido',
  },

  delivery: {
    allClear: 'Los avisos por email están saliendo. Revisado a las {time}.',
    checkFailedTitle: 'No pudimos revisar si los avisos por email están saliendo',
    checkFailed: 'La consulta devolvió: {message}. Mientras tanto, no hay forma de saber si llegan.',
    title: 'Hay avisos por email que no están saliendo',
    invitesFailed: {
      one: '1 invitación de calendario dejó de reintentarse',
      other: '{count} invitaciones de calendario dejaron de reintentarse',
    },
    invitesStuck: {
      one: '1 invitación de calendario espera hace más de 30 minutos',
      other: '{count} invitaciones de calendario esperan hace más de 30 minutos',
    },
    referralsFailed: {
      one: '1 aviso de derivación respondida dejó de reintentarse',
      other: '{count} avisos de derivación respondida dejaron de reintentarse',
    },
    referralsStuck: {
      one: '1 aviso de derivación respondida espera hace más de 30 minutos',
      other: '{count} avisos de derivación respondida esperan hace más de 30 minutos',
    },
    billingSummariesFailed: {
      one: '1 resumen de facturación dejó de reintentarse',
      other: '{count} resúmenes de facturación dejaron de reintentarse',
    },
    billingSummariesStuck: {
      one: '1 resumen de facturación espera hace más de dos horas',
      other: '{count} resúmenes de facturación esperan hace más de dos horas',
    },
    sessionRequestsFailed: {
      one: '1 aviso de turno (pedido, respuesta o cancelación) dejó de reintentarse',
      other: '{count} avisos de turnos (pedidos, respuestas o cancelaciones) dejaron de reintentarse',
    },
    sessionRequestsStuck: {
      one: '1 aviso de turno (pedido, respuesta o cancelación) espera hace más de 30 minutos',
      other: '{count} avisos de turnos (pedidos, respuestas o cancelaciones) esperan hace más de 30 minutos',
    },
    treatmentNoticesFailed: {
      one: '1 aviso de pedido de atención o de cancelaciones seguidas dejó de reintentarse',
      other: '{count} avisos de pedidos de atención o de cancelaciones seguidas dejaron de reintentarse',
    },
    treatmentNoticesStuck: {
      one: '1 aviso de pedido de atención o de cancelaciones seguidas espera hace más de 30 minutos',
      other: '{count} avisos de pedidos de atención o de cancelaciones seguidas esperan hace más de 30 minutos',
    },
    remindersLate: {
      one: '1 recordatorio de turno está atrasado',
      other: '{count} recordatorios de turno están atrasados',
    },
    noteRemindersLate: {
      one: '1 aviso de evolución sin cargar está atrasado',
      other: '{count} avisos de evolución sin cargar están atrasados',
    },
    httpErrors: {
      one: '1 llamada al servidor de envíos falló en las últimas horas',
      other: '{count} llamadas al servidor de envíos fallaron en las últimas horas',
    },
    cronFailures: {
      one: '1 tarea programada falló en las últimas 24 horas',
      other: '{count} tareas programadas fallaron en las últimas 24 horas',
    },
    hint: 'Revisá en Vercel las variables SMTP_* y REMINDER_CRON_SECRET, y en Supabase (Vault) las direcciones de envío y ese mismo secreto.',
  },

  family: {
    addMinor: 'Agregar a un menor a tu cargo',
    addMinorHint: 'Un hijo, una hija o cualquier menor a tu cargo: vas a ver sus turnos y reservar por él o ella. Una persona adulta tiene que tener su propia cuenta.',
    minorsOnly: 'Solo se agregan menores de 18 años. Una persona adulta se registra con su propio correo.',
    fillRequired: 'Completá nombre, apellido y fecha de nacimiento.',
    professionalHint: 'El profesional tiene que aceptarlo antes de que puedas reservar.',
    confirmRemove: '¿Quitar a {name} de tu grupo familiar?',
    remove: 'Quitar',
    requestProfessional: 'Pedir otro profesional',
    requestProfessionalHint: 'El profesional tiene que aceptar el pedido antes de que puedas reservar. No ve con qué otros profesionales se atiende.',
    requestPick: 'Elegí un profesional',
    requestSend: 'Enviar pedido',
    requestSent: 'Enviamos el pedido a {name}.',
    noOtherProfessionals: 'No hay otros profesionales disponibles.',
    withdraw: 'Retirar el pedido',
    confirmWithdraw: '¿Retirar el pedido a {name}?',
    referredBy: 'Derivación de {name}',
    declineReason: 'Motivo: {reason}',
    unknownProfessional: 'Profesional',
  },

approvals: {
    subtitle: 'Pacientes que pidieron atenderse con vos. Hasta que los aceptes, no pueden reservar turnos.',
    none: 'No hay solicitudes pendientes',
    noneHint: 'Cuando alguien pida atenderse con vos, va a aparecer acá.',
    requestedAt: 'Solicitado',
    accountHolder: 'Titular de la cuenta',
    contact: 'Contacto',
    particularHint: 'Sin obra social: acordá el valor de la sesión con el paciente.',
    declineTitle: 'Rechazar solicitud',
    declineBody: '¿Rechazar la solicitud de {name}? Va a poder pedir otro profesional.',
    declineReason: 'Motivo',
    declineReasonHint: 'Lo ve el paciente. Si es una derivación, a quien derivó solo le avisamos que no aceptaste.',
    sessionRequestsTitle: 'Pedidos de turno',
    sessionRequestsHint: 'Turnos que pidieron tus pacientes en horarios que necesitan tu aprobación. Si no respondés hasta 12 horas antes, el pedido se cancela solo y le avisamos al paciente.',
    patientRequestsTitle: 'Pedidos para atenderse con vos',
    lapsesAt: 'Vence',
    confirmSession: 'Confirmar',
    sessionDeclineTitle: 'Rechazar el pedido de turno',
    sessionDeclineBody: '¿Rechazar el turno que pidió {name} para el {when}? El horario vuelve a quedar libre.',
    sessionDeclineReasonHint: 'Se lo mandamos al paciente en el correo.',
    requestedByPatient: 'Lo pidió el paciente',
    referredBy: 'Derivación de {name}',
    referralNote: 'Motivo de la derivación',
  },

  subscriptions: {
    title: 'Suscripciones',
    monthlyFee: 'Cuota mensual',
    annualFee: 'Cuota anual',
    billingPeriod: 'Período',
    periodMonthly: 'Mensual',
    periodAnnual: 'Anual',
    markPaid: 'Marcar como pagada',
    overdueAlert: {
      one: '{count} profesional con la cuota vencida',
      other: '{count} profesionales con la cuota vencida',
    },
    feeByDiscipline: 'Cuota por disciplina',
    blockProfessional: 'Bloquear profesional',
    autoBlockDisabled: 'El bloqueo automático está desactivado',
    subtitle: 'Lo que cada profesional le paga al consultorio por usar el sistema.',
    periodTitle: 'Cuotas del período',
    generate: 'Generar cuotas del mes',
    generated: 'Cuotas nuevas: {created}. Actualizadas por cambio de precio: {updated}.',
    noPeriods: 'No hay cuotas generadas para este mes',
    noPeriodsHint: 'Usá "Generar cuotas del mes" para crearlas a partir de la disciplina de cada profesional.',
    collected: 'Cobrado',
    outstanding: 'Pendiente',
    overdueTitle: 'Cuotas vencidas',
    manualBlockNote: 'El bloqueo es manual: revisá y bloqueá vos si corresponde.',
    blockedForNonPayment: 'Cuota impaga',
    pricingHint: 'La cuota es la misma para todos los profesionales de una disciplina. Dejá vacío el valor anual si no ofrecés esa modalidad.',
    annualPlaceholder: 'Sin plan anual',
    missingDisciplineTitle: 'Cuotas en $0',
    missingDisciplineBody: { one: 'Hay 1 profesional sin disciplina asignada, así que su cuota queda en $0. Marcarla como pagada no cambia los totales.', other: 'Hay {count} profesionales sin disciplina asignada, así que su cuota queda en $0. Marcarlas como pagadas no cambia los totales.' },
    assignDiscipline: 'Asignar disciplina',
    zeroFeeHint: 'Sin disciplina asignada',
    reopen: 'Deshacer el pago',
    confirmReopen: '¿Deshacer el pago de {name}? La cuota vuelve a quedar pendiente.',
  },

professionals: {
    subtitle: 'Quiénes atienden en el consultorio, dónde y con qué disciplina.',
    noHours: 'Sin horarios cargados',
    placesHint: 'Cada profesional elige sus sedes al cargar sus horarios en Configuración → Horarios.',
    sessionLengthHint: 'La pone cada profesional en Configuración → Horarios.',
    add: 'Agregar profesional',
    none: 'Todavía no hay profesionales cargados',
    sendInvite: 'Crear',
    invited: 'Listo. {email} va a quedar vinculado cuando se registre con ese correo.',
    inviteExplanation: 'Vos cargás los datos y el profesional crea su propia contraseña registrándose con este correo. Su cuenta se vincula sola la primera vez que entra.',
    emailHint: 'Tiene que ser el correo con el que se va a registrar.',
    disciplineHint: 'Define la cuota de suscripción que le corresponde.',
    locationsHint: 'Dónde atiende. Se usa para el check-in por QR.',
    license: 'Matrícula',
    noDiscipline: 'Sin disciplina',
    pendingClaim: 'Falta que se registre',
    pendingRequests: 'Postulaciones pendientes',
    pendingSubtitle: 'Profesionales que se postularon solos. No pueden trabajar hasta que los apruebes.',
    requestedAt: 'Se postuló',
    feeAccepted: 'Cuota aceptada',
    verifyLicenseHint: 'Verificá la matrícula por fuera del sistema antes de aprobar: acá se registra lo que declaró, no se valida.',
    confirmApprove: '¿Aprobar a {name}? Va a poder atender y aparecer entre los profesionales.',
    confirmReject: '¿Rechazar la postulación de {name}?',
    approved: 'Aprobaste a {name}.',
    rejected: 'Rechazaste la postulación de {name}.',
    active: 'Activo',
    saved: 'Guardamos los cambios.',
    confirmBlock: '¿Bloquear a {name}? No va a poder entrar al sistema.',
    confirmUnblock: '¿Desbloquear a {name}?',
  },

  settings: {
    title: 'Configuración',
    workingHours: 'Horarios de atención',
    sessionLength: 'Duración de la sesión',
    minutes: 'minutos',
    blockTime: 'Bloquear horario',
    blockTypes: {
      blocked: 'Bloqueado',
      vacation: 'Vacaciones',
      personal: 'Personal',
      meeting: 'Reunión',
    },
    notificationChannel: 'Canal de notificaciones',
    // Configuración: a professional's own tabs, and admin's Consultorio (0060).
    subtitleTherapist: 'Tus datos, tus horarios, lo que cobrás y los avisos. Cada tarjeta se guarda con su propio botón.',
    subtitleAdmin: 'Lo tuyo como profesional y, en Consultorio, lo que vale para todo el consultorio. Cada tarjeta se guarda con su propio botón.',
    tabs: {
      details: 'Mis datos',
      hours: 'Horarios',
      charges: 'Cobros',
      notices: 'Avisos',
      practice: 'Consultorio',
    },
    // A card changed and not saved (lib/unsavedChanges.js).
    unsaved: 'Sin guardar',
    unsavedConfirm: 'Hay cambios sin guardar. ¿Salir igual y perderlos?',
    cardSaved: 'Guardamos los cambios.',
    loadFailed: 'No pudimos cargar la configuración',
    clockTitle: 'Hora y moneda',
    clockFixed: 'No se cambian desde acá. Los horarios de atención son horas de reloj en esta zona y los turnos ya agendados son momentos fijos, así que otra zona correría todos los horarios contra lo que ya está reservado. Otra moneda no convierte ningún monto.',
    timezone: 'Zona horaria',
    currency: 'Moneda',
    locale: 'Formato de fechas y números',
    remindersTitle: 'Recordatorio al paciente',
    remindersHint: 'Para los profesionales que no pusieron su propio valor en Configuración → Avisos.',
    reminderHours: 'Se manda',
    hoursBefore: 'horas antes del turno',
    ownReminders: {
      one: '1 profesional usa su propio valor.',
      other: '{count} profesionales usan su propio valor.',
    },
    reminderHoursInvalid: 'El recordatorio tiene que ser un número entero de horas entre 1 y 168.',
    checkinTitle: 'Check-in con QR',
    checkinHint: 'Cuándo el QR de una sede acepta la llegada de un paciente.',
    checkinOpens: 'Abre',
    minutesBefore: 'minutos antes del turno',
    checkinCloses: 'Cierra',
    minutesAfter: 'minutos después de la hora del turno',
    checkinInvalid: 'El check-in abre entre 0 y 720 minutos antes y cierra entre 0 y 240 minutos después.',
    lastChanged: 'Última modificación: {when}',
    saved: 'Guardamos la configuración.',
    // Admin's block on a whole patient account (0062).
    accountBlockTitle: 'Bloquear la cuenta de un paciente',
    accountBlockHint: 'La persona no puede entrar a la app, con ningún profesional. Para abuso o fraude. Solo ves su nombre, su correo y el bloqueo, nunca sus turnos.',
    accountNotFound: 'No hay ninguna cuenta con ese correo.',
    accountSignsIn: 'entra a su cuenta',
    accountNeverSignedIn: 'nunca entró a su cuenta',
    accountBlocked: 'Bloqueada',
    accountActive: 'Activa',
    accountBlockedSince: 'Bloqueada el {when}. Motivo: {reason}',
    blockReason: 'Motivo',
    blockReasonHint: 'Queda guardado con el bloqueo.',
    blockAccount: 'Bloquear cuenta',
    unblockAccount: 'Desbloquear cuenta',
    confirmBlockAccount: '¿Bloquear la cuenta de {name}? No va a poder entrar a la app hasta que la desbloquees.',
  },

  weekdays: {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo',
  },
};

export default es;
